/**
 * 服务类型编辑。
 *
 * 让店主自己维护「做哪些服务、要多久、多少钱、走几步」，
 * 而不是每次改个交期都要找开发。
 *
 * 唯一不让他改的是流程里那个「交付给顾客」步骤必须有且只有一个 ——
 * 没有它，工单永远走不到已交付，也就永远收不了尾款、进不了欠款清单。
 * 那是账目层面的洞，不是体验问题。
 */

import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { Button, Drawer, Field, Input, MoneyInput, Segmented, cx, useToast } from '../components/ui'
import { parseAmount } from '../lib/format'

export type StepKind = 'VISIT' | 'WORK' | 'DELIVER'

export interface FlowStep {
  title: string
  kind: StepKind
}

export interface ServiceType {
  id: string
  code: string
  nameZh: string
  nameMs: string
  nameEn: string
  leadDays: number
  defaultAmount: number
  showsToothChart: boolean
  checkupMonths: number
  renewYears: number
  flowSteps: FlowStep[]
  sortOrder: number
  isActive: boolean
  caseCount: number
}

const KIND_LABEL: Record<StepKind, string> = {
  VISIT: '顾客到店',
  WORK: '店里制作',
  DELIVER: '交付给顾客',
}

const KIND_HINT: Record<StepKind, string> = {
  VISIT: '完成后会问你要不要约下次',
  WORK: '店里自己做，不打扰顾客；做完自动变成「可取件」',
  DELIVER: '做完这步工单就算已交付，可以收尾款',
}

const DEFAULT_FLOW: FlowStep[] = [
  { title: '咨询报价', kind: 'VISIT' },
  { title: '取终模', kind: 'VISIT' },
  { title: '生产中', kind: 'WORK' },
  { title: '试戴/交付', kind: 'DELIVER' },
]

export function ServiceTypeDrawer({
  open,
  onClose,
  item,
}: {
  open: boolean
  onClose: () => void
  /** 传了就是编辑 */
  item?: ServiceType | null
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const editing = Boolean(item)

  const [nameZh, setNameZh] = useState('')
  const [nameMs, setNameMs] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [leadDays, setLeadDays] = useState('7')
  const [price, setPrice] = useState('')
  const [tooth, setTooth] = useState(true)
  const [checkup, setCheckup] = useState('0')
  const [renew, setRenew] = useState('0')
  const [flow, setFlow] = useState<FlowStep[]>(DEFAULT_FLOW)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (item) {
      setNameZh(item.nameZh)
      setNameMs(item.nameMs)
      setNameEn(item.nameEn)
      setLeadDays(String(item.leadDays))
      setPrice(item.defaultAmount ? (item.defaultAmount / 100).toFixed(2) : '')
      setTooth(item.showsToothChart)
      setCheckup(String(item.checkupMonths ?? 0))
      setRenew(String(item.renewYears ?? 0))
      setFlow(item.flowSteps.length ? item.flowSteps : DEFAULT_FLOW)
    } else {
      setNameZh('')
      setNameMs('')
      setNameEn('')
      setLeadDays('7')
      setPrice('')
      setTooth(true)
      setCheckup('0')
      setRenew('0')
      setFlow(DEFAULT_FLOW)
    }
    setError(null)
  }, [open, item])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        nameZh: nameZh.trim(),
        nameMs: nameMs.trim() || undefined,
        nameEn: nameEn.trim() || undefined,
        leadDays: Number(leadDays) || 0,
        defaultAmount: price.trim() ? (parseAmount(price) ?? 0) : 0,
        showsToothChart: tooth,
        checkupMonths: Number(checkup) || 0,
        renewYears: Number(renew) || 0,
        flowSteps: flow.map((s) => ({ title: s.title.trim(), kind: s.kind })),
      }
      return item
        ? api.patch(`/api/service-types/${item.id}`, body)
        : api.post('/api/service-types', body)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-types'] })
      void qc.invalidateQueries({ queryKey: ['case-meta'] })
      toast.show(editing ? '已保存' : '已新增')
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  const remove = useMutation({
    mutationFn: () => api.del(`/api/service-types/${item!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-types'] })
      void qc.invalidateQueries({ queryKey: ['case-meta'] })
      toast.show('已删除')
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  const toggleActive = useMutation({
    mutationFn: () => api.patch(`/api/service-types/${item!.id}`, { isActive: !item!.isActive }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['service-types'] })
      void qc.invalidateQueries({ queryKey: ['case-meta'] })
      toast.show(item!.isActive ? '已停用' : '已启用')
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  const setStep = (i: number, patch: Partial<FlowStep>) =>
    setFlow((f) => f.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))

  const move = (i: number, dir: -1 | 1) =>
    setFlow((f) => {
      const next = [...f]
      const j = i + dir
      if (j < 0 || j >= next.length) return f
      ;[next[i], next[j]] = [next[j]!, next[i]!]
      return next
    })

  const deliverCount = flow.filter((s) => s.kind === 'DELIVER').length
  const flowOk =
    flow.length > 0 &&
    flow.every((s) => s.title.trim()) &&
    deliverCount === 1 &&
    flow[flow.length - 1]?.kind === 'DELIVER'
  const canSave = nameZh.trim().length > 0 && flowOk

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title={editing ? `编辑「${item!.nameZh}」` : '新增服务类型'}
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            取消
          </Button>
          <Button
            size="lg"
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            保存
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error && (
          <div className="rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[16px] text-bad">
            {error}
          </div>
        )}

        <Field label="名称" required hint="开单时显示的名字">
          <Input
            autoFocus={!editing}
            value={nameZh}
            onChange={(e) => setNameZh(e.target.value)}
            placeholder="例：软衬"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="马来文名" hint="顾客链接页会用">
            <Input value={nameMs} onChange={(e) => setNameMs(e.target.value)} placeholder="选填" />
          </Field>
          <Field label="英文名">
            <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="选填" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="默认交期" hint="开单时自动算出承诺交期，可以改">
            <div className="flex items-center gap-3">
              <Input
                inputMode="numeric"
                value={leadDays}
                onChange={(e) => setLeadDays(e.target.value.replace(/\D/g, ''))}
                className="tnum w-24"
              />
              <span className="text-[17px] text-ink-2">天</span>
            </div>
          </Field>
          <Field label="默认报价" hint="留空则开单时不预填">
            <MoneyInput value={price} onChange={setPrice} />
          </Field>
        </div>

        <label className="flex min-h-12 cursor-pointer items-center gap-3 text-[17px]">
          <input
            type="checkbox"
            checked={tooth}
            onChange={(e) => setTooth(e.target.checked)}
            className="size-5.5 accent-brand-600"
          />
          <span>
            开单时展开牙位图
            <span className="block text-[15px] text-ink-3">维修、调整这类通常不需要标牙位</span>
          </span>
        </label>

        {/* ── 回访与复购 ── */}
        <div className="rounded-md border border-line bg-sunken p-4">
          <p className="text-[16px] font-semibold">自动跟进</p>
          <p className="mt-1 mb-3 text-[15px] text-ink-3">
            到点了系统会自动把顾客放进「待通知」清单，
            <strong>不会自动发出去</strong>，你点一下才发。填 0 就是不跟进。
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="交付后多久回访" hint="检查戴得合不合适">
              <div className="flex items-center gap-3">
                <Input
                  inputMode="numeric"
                  value={checkup}
                  onChange={(e) => setCheckup(e.target.value.replace(/\D/g, ''))}
                  className="tnum w-24"
                />
                <span className="text-[17px] text-ink-2">个月</span>
              </div>
            </Field>
            <Field label="多久之后该换新" hint="活动假牙一般 4–5 年">
              <div className="flex items-center gap-3">
                <Input
                  inputMode="numeric"
                  value={renew}
                  onChange={(e) => setRenew(e.target.value.replace(/\D/g, ''))}
                  className="tnum w-24"
                />
                <span className="text-[17px] text-ink-2">年</span>
              </div>
            </Field>
          </div>
        </div>

        {/* ── 流程 ── */}
        <Field label="流程步骤" required>
          <div className="flex flex-col gap-3">
            {flow.map((s, i) => (
              <div
                key={i}
                className="rounded-md border border-line bg-white p-3 shadow-xs"
              >
                <div className="flex items-center gap-2">
                  <span className="tnum flex size-7 shrink-0 items-center justify-center rounded-full bg-sunken text-[14px] font-bold text-ink-2">
                    {i + 1}
                  </span>
                  <Input
                    value={s.title}
                    onChange={(e) => setStep(i, { title: e.target.value })}
                    placeholder="步骤名称"
                    maxLength={40}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="上移"
                    icon={<ArrowUp size={17} />}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="下移"
                    icon={<ArrowDown size={17} />}
                    disabled={i === flow.length - 1}
                    onClick={() => move(i, 1)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="删除这一步"
                    icon={<Trash2 size={17} />}
                    className="text-ink-3 hover:text-bad"
                    disabled={flow.length <= 1}
                    onClick={() => setFlow((f) => f.filter((_, idx) => idx !== i))}
                  />
                </div>

                <div className="mt-2 pl-9">
                  <Segmented
                    value={s.kind}
                    onChange={(k) => setStep(i, { kind: k })}
                    ariaLabel={`第 ${i + 1} 步的类型`}
                    options={[
                      { value: 'VISIT', label: KIND_LABEL.VISIT },
                      { value: 'WORK', label: KIND_LABEL.WORK },
                      { value: 'DELIVER', label: KIND_LABEL.DELIVER },
                    ]}
                  />
                  <p className="mt-1.5 text-[14px] text-ink-3">{KIND_HINT[s.kind]}</p>
                </div>
              </div>
            ))}

            <Button
              icon={<Plus size={18} />}
              disabled={flow.length >= 12}
              onClick={() =>
                setFlow((f) => {
                  // 新步骤插在交付之前 —— 交付永远是最后一步
                  const next = [...f]
                  const at = Math.max(0, next.length - 1)
                  next.splice(at, 0, { title: '', kind: 'VISIT' })
                  return next
                })
              }
            >
              加一步
            </Button>
          </div>

          {!flowOk && (
            <p
              className={cx(
                'mt-3 rounded-md px-4 py-3 text-[15px]',
                'border border-warn-line bg-warn-bg text-warn',
              )}
            >
              {flow.some((s) => !s.title.trim())
                ? '每个步骤都要有名字'
                : deliverCount === 0
                  ? '流程里必须有一个「交付给顾客」的步骤 —— 没有它，工单永远走不到已交付，也就收不了尾款'
                  : deliverCount > 1
                    ? '只能有一个「交付给顾客」的步骤'
                    : '「交付给顾客」必须是最后一步'}
            </p>
          )}

          {editing && item!.caseCount > 0 && (
            <p className="mt-3 rounded-md bg-sunken px-4 py-3 text-[15px] text-ink-2">
              已有 {item!.caseCount} 张工单在用这个类型。
              <strong>改流程只影响以后新开的单</strong>，进行中的单保持原样 ——
              半路换掉一张单的流程，等于篡改它的历史。
            </p>
          )}
        </Field>

        {/* ── 停用 / 删除 ── */}
        {editing && (
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            <Button loading={toggleActive.isPending} onClick={() => toggleActive.mutate()}>
              {item!.isActive ? '停用（开单时不再出现）' : '重新启用'}
            </Button>
            <Button
              variant="ghost"
              icon={<Trash2 size={18} />}
              className="text-ink-3 hover:text-bad"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              删除
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  )
}
